import { FC, useRef, useState } from 'react';
import { AWARDS_VIDEOS } from '@/components/Awards/assets';
import { AwardsItem } from '@/components/Awards/interface';
import { isArrayNotEmpty } from '@/helpers/array';
import useDevice from '@/hooks/useDevice';
import { useLazyVideoSource } from '@/hooks/useLazyVideoSource';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { gsap } from '@/lib/gsap';
import css from './style.module.scss';
interface Props {
  items: AwardsItem[];
}
const AwardsList: FC<Props> = ({
  items
}) => {
  const device = useDevice();
  const [openedAwards, setOpenedAwards] = useState<number[]>([]);
  const awardsListRef = useRef<HTMLUListElement>(null);
  const awardContentRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const ulRefs = useRef<Record<number, HTMLUListElement | null>>({});
  const shouldLoadVideos = useLazyVideoSource(awardsListRef);
  useViewportVideosPlayback([awardsListRef]);
  const toggleOpened = (id: number) => {
    setOpenedAwards(prev => prev.includes(id) ? prev.filter(awardId => awardId !== id) : [...prev, id]);
  };
  const handleMouseEnter = (id_0: number) => {
    const ul = ulRefs.current[id_0];
    if (!ul) return;
    gsap.set(ul, {
      height: 'auto'
    });
    const fullHeight = ul.scrollHeight;
    gsap.fromTo(ul, {
      height: 0,
      opacity: 0
    }, {
      height: fullHeight,
      opacity: 1,
      duration: 0.3,
      ease: 'power2.out',
      onComplete: () => {
        gsap.to(ul.children, {
          autoAlpha: 1,
          y: 0,
          stagger: 0.1,
          duration: 0.4,
          ease: 'power2.out'
        });
      }
    });
  };
  const handleMouseLeave = (id_1: number) => {
    const ul_0 = ulRefs.current[id_1];
    if (!ul_0) return;
    gsap.to(ul_0.children, {
      autoAlpha: 0,
      y: 10,
      stagger: 0.05,
      duration: 0.2,
      ease: 'power2.out',
      onComplete: () => {
        gsap.to(ul_0, {
          height: 0,
          opacity: 0,
          duration: 0.3,
          ease: 'power2.inOut'
        });
      }
    });
  };
  return <>
			<ul ref={awardsListRef} className={css.awards}>
				{isArrayNotEmpty(items) && items.map(({
        type,
        amount,
        sign,
        awardsList,
        id: id_2
      }, index) => <li key={index} className={css.awardsItem} onMouseEnter={() => handleMouseEnter(id_2)} onMouseLeave={() => handleMouseLeave(id_2)}>
							<video loop muted playsInline preload="none" controls={false}>
								{shouldLoadVideos ? AWARDS_VIDEOS[type]?.map((source, index_0) => <source key={`video_${index_0}`} src={source} type={source.includes('mov') ? 'video/quicktime; codecs="hvc1"' : 'video/webm'} />) : null}
							</video>

							{device === 'desktop' && <div className={css.awardsContent}>
									<p>
										{amount}

										<span>{sign}</span>
									</p>

									<span>{type}</span>

									<ul ref={el => {
            ulRefs.current[id_2] = el;
            if (el) {
              ulRefs.current[id_2] = el;
              gsap.set(el, {
                opacity: 0,
                height: 0,
                overflow: 'hidden'
              });
              gsap.set(el.children, {
                autoAlpha: 0,
                y: 10
              });
            }
          }}>
										{isArrayNotEmpty(awardsList) && awardsList.map((award, index_1) => <li key={index_1}>
													{award.name}
													{award.date ? ', ' + award.date : null}
												</li>)}
									</ul>
								</div>}
						</li>)}
			</ul>

			{device === 'mobile' && <div className={css.awardsMobile}>
					{isArrayNotEmpty(items) && items.map(({
        id: id_3,
        amount: amount_0,
        sign: sign_0,
        type: type_0,
        awardsList: awardsList_0
      }, index_2) => {
        const isOpened = openedAwards.includes(id_3);
        const awardItem = awardContentRefs.current[id_3];
        const maxHeight = isOpened && awardItem ? `${awardItem.scrollHeight}px` : '0px';
        return <div key={`${id_3}_${index_2}`} className={css.mobileItem}>
									<div className={css.head}>
										<div className={css.title}>
											{amount_0}
											<span>{sign_0}</span>
											<div className={css.type}>{type_0}</div>
										</div>

										<button className={css.button} onClick={() => toggleOpened(id_3)}>
											{isOpened ? 'See Less' : 'See More'}
										</button>
									</div>

									<div ref={node => {
            awardContentRefs.current[id_3] = node;
          }} className={css.content} style={{
            maxHeight
          }}>
										<div className={css.list}>
											{isArrayNotEmpty(awardsList_0) && awardsList_0.map((award_0: any) => <div key={award_0.id} className={css.listItem}>
														<div className={css.info}>
															{award_0.name}
															{award_0?.additionalText ? <div className={css.additionalText}>{award_0.additionalText}</div> : null}
														</div>
														<div className={css.date}>{award_0.date}</div>
													</div>)}
										</div>
									</div>
								</div>;
      })}
				</div>}
		</>;
};
export default AwardsList;